import React, { useEffect, useMemo, useState } from 'react'
import { faBooks } from '@fortawesome/pro-solid-svg-icons'
import { RankmiTable } from '@rankmi/rankmi-table'

import { generateTableLocale } from 'helpers/translations.helper'
import { useTranslationRankmi, joinKeys } from 'helpers/translations.helper'
import { Course, ExternalCourse } from 'graphqlAPI'
import { canSkipOnRowSelected } from 'helpers'
import { useColumns } from './hooks/useColumns'
import { prefix } from './constants'
import { getKey } from './utils'
import styles from './index.module.scss'

type Id = Course['id'] | ExternalCourse['id']

type DataColumn =
  | (Course & {
      key: Id
      action: Id
    })
  | (ExternalCourse & {
      key: Id
      action: Id
    })

export type Data = Course | ExternalCourse

interface ITable {
  setPage(page: number): void
  setSearch(text: string): void
  setSort: (value: { field: string; order: 'ascend' | 'descend' }) => void
  setPerPage(perPage: number): void
  setSelectedRows(ids: string[]): void
  selectedRows: string[]
  setAllSelected(state: boolean): void
  list: Data[]
  total?: number
  loading: boolean
  currentPage: number
}

const Table: React.FC<ITable> = ({
  setPage,
  setSearch,
  setSort,
  setPerPage,
  setSelectedRows,
  selectedRows,
  setAllSelected,
  list,
  loading,
  total,
  currentPage,
}) => {
  const tRelative = useTranslationRankmi({
    prefix: joinKeys(prefix),
  })
  const [dataSource, setDataSource] = useState<DataColumn[]>([])

  const translations = useMemo(
    () =>
      generateTableLocale({
        header: {
          rowsCount: tRelative('rowsCount'),
          rowsCount_plural: tRelative('rowsCount_plural'),
        },
      }),
    [tRelative]
  )

  useEffect(() => {
    setDataSource(
      list.map(({ id, ...rest }) => ({
        id,
        key: getKey({ id, __typename: rest.__typename }),
        ...rest,
        action: id,
      }))
    )
  }, [list])

  const columns = useColumns()

  return (
    <div className={styles.container}>
      <RankmiTable
        columnHeaders={columns}
        data={dataSource}
        selectedRows={selectedRows}
        total={total}
        currentPage={currentPage}
        changeColumnsVisibility
        onPaginationChange={(page: any) => setPage(page)}
        onSearch={(text: string) => setSearch(text)}
        onSortChange={(sortData: any) => setSort(sortData)}
        loading={loading}
        onRowsPerPageChange={setPerPage}
        onRowSelected={({
          allSelected,
          unselectedRowKeys,
          selectedRowKeys,
          dispatcher,
        }: any) => {
          if (canSkipOnRowSelected(dispatcher)) return
          setSelectedRows(allSelected ? unselectedRowKeys : selectedRowKeys)
          setAllSelected(allSelected)
        }}
        locale={translations}
        countIcon={faBooks}
        initialRowsPerPage={10}
        simpleSelectAll
        responsive
      />
    </div>
  )
}

export default Table
